/**
 * Component: Toggle
 * ------------------------------------------------------------------------------
 * A accessible function to trigger a toggle-able component with a window overlay.
 *
 * @namespace Toggle
 *
 */

import debounce from 'lodash-es/debounce';
import log from 'salvo-lite/log';
import * as a11y from '@shopify/theme-a11y';
import cssClasses from '../helpers/cssClasses';
import { on } from '../helpers/utils';
import bind from '../mixins/bind';
import escEvent from '../mixins/esc-event';
import overlay from '../mixins/overlay';
import breakpoints from '../helpers/breakpoints';

class Toggle {
  constructor(theme, elem) {
    this._theme = theme;
    this._elem = elem;
    this._settings = {
      // List of all toggles that would need closing if another opens
      closeTargets: [
        'cartDrawerTarget',
        'menuDrawerTarget',
        'navDropdownTarget',
        'navDropdownTargetVehicle',
        'navDropdownTargetAccessories',
        'navDropdownTargetMega',
        'searchTarget',
        'vinTarget',
      ],
      desktopOnly: false,
      disableScroll: false,
      disableSearchScroll: false,
      focusInput: true,
      isNavDropdownToggle: false,
      overlay: true,
      html: 'html',
      body: 'body',
      targetSelector: null,
      toggleSelector: null,
      ...JSON.parse(elem.getAttribute('data-toggle') || '{}'),
    };
    // console.log('[TOGGLE DATA]', elem.getAttribute('data-toggle'));
    /**
     * Node selectors.
     */
    this._nodeSelectors = {
      html: document.querySelector(this._settings.html),
      body: document.querySelector(this._settings.body),
      target: document.querySelectorAll(
        `[js-toggle="${this._settings.targetSelector}"]`
      ),
      toggle: document.querySelectorAll(
        `[js-toggle="${this._settings.toggleSelector}"]`
      ),
    };

    /**
     * Initialise component bind to prevent double binds.
     */
    this._binder = bind(this._nodeSelectors.toggle, {
      className: 'toggle-set',
    });

    log.debug(this.constructor.name, 'Constructed', this);
  }

  onInit() {
    log.debug(this.constructor.name, 'Initiating');
    if (this._binder && this._binder.isSet()) {
      return;
    }

    this._setToggleEvents();
    this._setListeners();
    this._binder.set();

    log.debug(this.constructor.name, 'Initiated');
  }

  /**
   * Set component listeners.
   */
  _setListeners() {
    window.Frame.EventBus.listen(
      ['EscEvent:on', `Overlay:${this._settings.targetSelector}:hide`],
      (response) => {
        this._close(response);
      }
    );
  }

  /**
   * Set click events on toggle selectors.
   */
  _setToggleEvents() {
    this._nodeSelectors.toggle.forEach((element) => {
      on('click', element, (event) => this._handleToggleEvent(event, element));
    });

    on(
      'resize',
      debounce(() => this._handleResizeEvent(), 250)
    );
  }

  /**
   * Handle nav closing when resizing the window from mobile to desktop size.
   */
  _handleResizeEvent() {
    if (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches) return;

    this._close(window);
    overlay({ namespace: this._settings.targetSelector }).close();
  }

  /**
   * Handle toggle events.
   * @param {Event} event Click event.
   */
  _handleToggleEvent(event, el) {
    if (
      window.matchMedia(`(min-width: ${breakpoints.medium})`).matches &&
      this._settings.desktopOnly
    ) {
      event.preventDefault();
    }
    if (
      !el.classList.contains('vin-lookup-component') ||
      (el.classList.contains('vin-lookup-component') &&
        !document.querySelector('.current-car-set'))
    ) {
      if (this._settings.isNavDropdownToggle) {
        this._handleNavDropdownToggleEvent(el);
      } else {
        this._toggle(el);
      }
    }
  }

  /**
   * Handle desktop nav dropdown toggle event.
   */
  _handleNavDropdownToggleEvent(element) {
    if (element.classList.contains(cssClasses.active)) {
      this._close(element);
    } else {
      this._removeSelectorClasses(element);
      this._toggle(element);
    }
  }

  /**
   * Toggle target component.
   * @param {Object} element the element to open, only required when isNavDropdownToggle is set to true.
   */
  _toggle(element) {
    return this._isTargetActive(element) ? this._close() : this._open(element);
  }

  /**
   * Open target component and fire global open event.
   * @param {Object} element the element to open, only required when isNavDropdownToggle is set to true.
   */
  _open(element) {
    const cutoff = parseInt(breakpoints.medium.replace('px', '')) - 1;
    if (
      window.matchMedia(`(max-width: ${cutoff}px)`).matches &&
      this._settings.desktopOnly
    )
      return;
    window.Frame.EventBus.emit(
      `Toggle:${this._settings.targetSelector}:open`,
      element
    );

    this._addSelectorClasses(element);
    escEvent().set();
    // this._focusTarget()
    this._lockScroll();
    // this._closeOtherToggles(element)

    if (this._settings.overlay) {
      overlay({ namespace: this._settings.targetSelector }).open();
    }
  }

  /**
   * Close target component and fire global close event.
   */
  _close(response) {
    this._removeSelectorClasses();
    // this._unfocusTarget()
    if (response !== 'other') {
      this._unlockScroll();
    }

    if (this._settings.overlay && response !== 'other') {
      overlay({ namespace: this._settings.targetSelector }).close();
    }

    window.Frame.EventBus.emit(`Toggle:${this._settings.targetSelector}:close`);
  }

  /**
   * Set toggle and target states active.
   * @param {Object} element the element to open, only required when isNavDropdownToggle is set to true.
   */
  _addSelectorClasses(element) {
    let thisTargetSelector = '';

    if (this._settings.isNavDropdownToggle) {
      if (element) {
        thisTargetSelector = element.querySelector(
          `[js-toggle="${this._settings.targetSelector}"]`
        );
      }

      this._nodeSelectors.target.forEach((item) => {
        if (item === thisTargetSelector) {
          item.classList.add(cssClasses.active);
        }
      });

      this._nodeSelectors.toggle.forEach((item) => {
        if (element === item) {
          item.classList.add(cssClasses.active);
        }
      });
    } else {
      this._nodeSelectors.target.forEach((item) => {
        item.classList.add(cssClasses.active);
      });

      this._nodeSelectors.toggle.forEach((item) => {
        item.classList.add(cssClasses.active);
      });
    }
  }

  /**
   * Remove active classes from all toggles and targets.
   */
  _removeSelectorClasses() {
    this._nodeSelectors.target.forEach((element) => {
      element.classList.remove(cssClasses.active);
    });

    this._nodeSelectors.toggle.forEach((element) => {
      element.classList.remove(cssClasses.active);
    });
  }

  /**
   * Lock the body scroll.
   */
  _lockScroll() {
    if (this._settings.disableScroll) {
      this._nodeSelectors.html.classList.add(cssClasses.locked);
      this._nodeSelectors.body.classList.add(cssClasses.locked);
    }

    if (this._settings.disableSearchScroll) {
      this._nodeSelectors.html.classList.add(cssClasses.searchLocked);
      this._nodeSelectors.body.classList.add(cssClasses.searchLocked);
    }
  }

  /**
   * Unlock the body scroll.
   */
  _unlockScroll() {
    if (this._settings.disableScroll) {
      this._nodeSelectors.html.classList.remove(cssClasses.locked);
      this._nodeSelectors.body.classList.remove(cssClasses.locked);
    }

    if (this._settings.disableSearchScroll) {
      this._nodeSelectors.html.classList.remove(cssClasses.searchLocked);
      this._nodeSelectors.body.classList.remove(cssClasses.searchLocked);
    }
  }

  /**
   * Check if target is active.
   */
  _isTargetActive(element) {
    return element.classList.contains(cssClasses.active);
  }

  /**
   * Set a11y focus on first target.
   */
  _focusTarget() {
    if (
      this._settings.focusInput &&
      this._targetHasInput(this._nodeSelectors.target[0])
    ) {
      a11y.trapFocus(this._nodeSelectors.target.querySelector('input'));
      return;
    }

    a11y.trapFocus(this._nodeSelectors.targetSelector);
  }

  /**
   * Check if focus has an input to focus on.
   */
  _targetHasInput(target) {
    return target.querySelector('input');
  }

  /**
   * Remove a11y focus on target.
   */
  _unfocusTarget() {
    a11y.removeTrapFocus(this._settings.targetSelector);
  }

  /**
   * Go through list of other toggles, emitting close events.
   */
  _closeOtherToggles() {
    this._settings.closeTargets.forEach((closeName) => {
      if (closeName !== this._settings.toggleSelector) {
        window.Frame.EventBus.emit(`Overlay:${closeName}:hide`);
      }
    });
  }

  /**
   * Return immutable object.
   */
  // return Object.freeze({
  //     init,
  //     open,
  //     close,
  // })
}

export default Toggle;



// WEBPACK FOOTER //
// ./src/resources/scripts/components/Toggle.js